import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { ArrowLeft, Trash2 } from "lucide-react";
import type { Partner, Contact, Account, Deal, PartnerReferrer } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InlineTextField, InlineSelectField, InlineTextareaField } from "@/components/inline-edit";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { PartnerReferrersPanel } from "@/components/panels/PartnerReferrersPanel";
import { PartnerReferralsPanel } from "@/components/panels/PartnerReferralsPanel";
import { PartnerCommissionsPanel } from "@/components/panels/partner-commissions-panel";
import { PartnerRctiPanel } from "@/components/panels/partner-rcti-panel";
import { PartnerSplitConfigPanel } from "@/components/panels/partner-split-config-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useTabState } from "@/hooks/use-tab-state";

const PARTNER_TYPE_OPTIONS = [
  { value: "mortgage_broker", label: "Mortgage Broker" },
  { value: "accountant", label: "Accountant" },
  { value: "financial_planner", label: "Financial Planner" },
  { value: "lawyer", label: "Lawyer" },
  { value: "real_estate_agent", label: "Real Estate Agent" },
  { value: "business_broker", label: "Business Broker" },
  { value: "other", label: "Other" },
];

const PARTNER_TIER_OPTIONS = [
  { value: "platinum", label: "Platinum" },
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" },
  { value: "bronze", label: "Bronze" },
];

const PARTNER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-700 dark:text-green-400",
  inactive: "bg-gray-500/10 text-gray-700 dark:text-gray-400",
  suspended: "bg-red-500/10 text-red-700 dark:text-red-400",
};

export default function PartnerDetail() {
  const [, params] = useRoute("/partners/:id");
  const [, navigate] = useLocation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useTabState("overview");
  const { toast } = useToast();

  const { data: partner, isLoading } = useQuery<Partner>({
    queryKey: ["/api/crm/partners", params?.id],
    enabled: !!params?.id,
  });

  // Query for contacts referred by this partner
  const { data: contactsData } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { status: "all" }],
    enabled: !!partner,
  });
  const referredContacts = Array.isArray(contactsData)
    ? contactsData.filter((c) => c.partnerId === partner?.id)
    : [];

  // Query for accounts referred by this partner
  const { data: accountsData } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { status: "all" }],
    enabled: !!partner,
  });
  const referredAccounts = Array.isArray(accountsData)
    ? accountsData.filter((a) => a.partnerId === partner?.id)
    : [];

  // Query for deals linked to referred contacts or accounts
  const { data: dealsData } = useQuery<Deal[]>({
    queryKey: ["/api/crm/deals"],
    enabled: !!partner,
  });
  const referredContactIds = referredContacts.map((c) => c.id);
  const referredAccountIds = referredAccounts.map((a) => a.id);
  // Filter deals by borrowerId (using borrowerType to determine if it's a contact or account)
  const referredDeals = Array.isArray(dealsData)
    ? dealsData.filter((d) =>
        (d.borrowerType === 'contact' && referredContactIds.includes(d.borrowerId)) ||
        (d.borrowerType === 'account' && referredAccountIds.includes(d.borrowerId))
      )
    : [];

  // Query for partner referrers (uses default queryFn with auth headers)
  const { data: partnerReferrers = [] } = useQuery<PartnerReferrer[]>({
    queryKey: ["/api/crm/partners", params?.id, "referrers"],
    enabled: !!params?.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Partner>) => {
      const response = await apiRequest("PATCH", `/api/crm/partners/${params?.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/partners", params?.id] });
      const previousPartner = queryClient.getQueryData(["/api/crm/partners", params?.id]);
      if (previousPartner) {
        queryClient.setQueryData(["/api/crm/partners", params?.id], {
          ...previousPartner,
          ...updates,
        });
      }
      return { previousPartner };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousPartner) {
        queryClient.setQueryData(["/api/crm/partners", params?.id], context.previousPartner);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/partners", params?.id] });
    },
  });

  const handleFieldUpdate = async (field: keyof Partner, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/crm/partners/${params?.id}`);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/partners"] });
      toast({
        title: "Partner deleted",
        description: "The partner has been successfully deleted.",
      });
      navigate("/partners");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete partner",
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
    setIsDeleteDialogOpen(false);
  };

  const handleNavItemClick = (href: string) => {
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading partner...</p>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The partner you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/partners")} size="sm">Back to Partners</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DetailPageShell
        title={partner.partnerName}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Partners", href: "/partners" },
          { label: partner.partnerName },
        ]}
        value={activeTab}
        onValueChange={setActiveTab}
        onNavItemClick={handleNavItemClick}
        badge={
          <Badge
            variant="secondary"
            className={`capitalize text-xs ${STATUS_COLORS[partner.status as keyof typeof STATUS_COLORS] || STATUS_COLORS.active}`}
          >
            {partner.status || "Active"}
          </Badge>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Contacts", href: "#contacts" },
          { label: "Referrals", href: "#referrals" },
          { label: "Commissions", href: "#commissions" },
          { label: "RCTI Records", href: "#rcti" },
          { label: "Split Config", href: "#split-config" },
          { label: "Activity", href: "#activity" },
        ]}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate("/partners")} data-testid="button-back">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div id="overview" className="space-y-2">
                {/* Core Details */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">PARTNER DETAILS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={partner.partnerName}
                        onSave={(value) => handleFieldUpdate("partnerName", value)}
                        label="Partner Name"
                        placeholder="Enter partner name"
                      />
                      <InlineTextField
                        value={partner.companyName}
                        onSave={(value) => handleFieldUpdate("companyName", value || null)}
                        label="Company Name"
                        placeholder="Optional"
                      />
                      <InlineSelectField
                        value={partner.partnerType || ""}
                        options={PARTNER_TYPE_OPTIONS}
                        onSave={(value) => handleFieldUpdate("partnerType", value || null)}
                        label="Partner Type"
                        placeholder="Select type"
                      />
                      <InlineSelectField
                        value={partner.partnerTier || ""}
                        options={PARTNER_TIER_OPTIONS}
                        onSave={(value) => handleFieldUpdate("partnerTier", value || null)}
                        label="Partner Tier"
                        placeholder="Select tier"
                      />
                      <InlineTextField
                        value={partner.abn}
                        onSave={(value) => handleFieldUpdate("abn", value || null)}
                        label="ABN"
                        placeholder="11 digit ABN"
                      />
                      <InlineSelectField
                        value={partner.status || "active"}
                        options={PARTNER_STATUS_OPTIONS}
                        onSave={(value) => handleFieldUpdate("status", value)}
                        label="Status"
                        placeholder="Select status"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CONTACT INFORMATION</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={partner.email}
                        onSave={(value) => handleFieldUpdate("email", value || null)}
                        label="Email"
                        placeholder="email@example.com"
                      />
                      <InlineTextField
                        value={partner.phone}
                        onSave={(value) => handleFieldUpdate("phone", value || null)}
                        label="Phone"
                        placeholder="Business phone"
                      />
                      <InlineTextField
                        value={partner.mobile}
                        onSave={(value) => handleFieldUpdate("mobile", value || null)}
                        label="Mobile"
                        placeholder="Mobile phone"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Address */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ADDRESS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <InlineTextField
                          value={partner.streetAddress}
                          onSave={(value) => handleFieldUpdate("streetAddress", value || null)}
                          label="Street Address"
                          placeholder="Enter street address"
                        />
                      </div>
                      <InlineTextField
                        value={partner.suburb}
                        onSave={(value) => handleFieldUpdate("suburb", value || null)}
                        label="Suburb"
                        placeholder="Suburb"
                      />
                      <InlineTextField
                        value={partner.state}
                        onSave={(value) => handleFieldUpdate("state", value || null)}
                        label="State"
                        placeholder="State"
                      />
                      <InlineTextField
                        value={partner.postcode}
                        onSave={(value) => handleFieldUpdate("postcode", value || null)}
                        label="Postcode"
                        placeholder="Postcode"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">NOTES</h3>
                    <InlineTextareaField
                      value={partner.notes}
                      onSave={(value) => handleFieldUpdate("notes", value || null)}
                      label="Notes"
                      placeholder="Additional notes about this partner"
                    />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "contacts",
            label: `Contacts (${partnerReferrers.length})`,
            content: (
              <div id="contacts" className="space-y-2">
                <Card>
                  <CardContent className="pt-4">
                    <PartnerReferrersPanel partnerId={partner.id} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "referrals",
            label: "Referrals",
            content: (
              <div id="referrals" className="space-y-2">
                <Card>
                  <CardContent className="pt-4">
                    <PartnerReferralsPanel partnerId={partner.id} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "commissions",
            label: "Commissions",
            content: (
              <div id="commissions" className="space-y-2">
                {/* Commission Rates */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">COMMISSION RATES</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={partner.upfrontCommissionRate?.toString() ?? null}
                        onSave={(value) => handleFieldUpdate("upfrontCommissionRate", value || null)}
                        label="Upfront Commission (%)"
                        placeholder="20.00"
                      />
                      <InlineTextField
                        value={partner.trailCommissionRate?.toString() ?? null}
                        onSave={(value) => handleFieldUpdate("trailCommissionRate", value || null)}
                        label="Trail Commission (%)"
                        placeholder="20.00"
                      />
                      <InlineTextField
                        value={partner.paymentTermsDays?.toString() ?? null}
                        onSave={(value) => handleFieldUpdate("paymentTermsDays", value ? parseInt(value) : null)}
                        label="Payment Terms (Days)"
                        placeholder="7"
                      />
                    </div>
                    <div className="mt-3">
                      <InlineTextareaField
                        value={partner.commissionNotes}
                        onSave={(value) => handleFieldUpdate("commissionNotes", value || null)}
                        label="Commission Notes"
                        placeholder="Special commission arrangements..."
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Commission Transactions Panel */}
                <Card>
                  <CardContent className="pt-4">
                    <PartnerCommissionsPanel partnerId={partner.id} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "rcti",
            label: "RCTI Records",
            content: (
              <div id="rcti" className="space-y-2">
                {/* RCTI Settings */}
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">RCTI SETTINGS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={partner.rctiRecipientName}
                        onSave={(value) => handleFieldUpdate("rctiRecipientName", value || null)}
                        label="RCTI Recipient Name"
                        placeholder="If different from partner name"
                      />
                      <InlineTextField
                        value={partner.rctiEmail}
                        onSave={(value) => handleFieldUpdate("rctiEmail", value || null)}
                        label="RCTI Email"
                        placeholder="Where to send RCTIs"
                      />
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <p className="text-sm font-medium">GST Registered</p>
                          <p className="text-xs text-muted-foreground">
                            Is this partner GST registered?
                          </p>
                        </div>
                        <Badge variant={partner.gstRegistered ? "default" : "secondary"}>
                          {partner.gstRegistered ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* RCTI Records Panel */}
                <Card>
                  <CardContent className="pt-4">
                    <PartnerRctiPanel partnerId={partner.id} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "split-config",
            label: "Split Config",
            content: (
              <div id="split-config" className="space-y-2">
                <Card>
                  <CardContent className="pt-4">
                    <PartnerSplitConfigPanel
                      partnerId={partner.id}
                      partner={{
                        id: partner.id,
                        upfrontCommissionRate: partner.upfrontCommissionRate?.toString() ?? null,
                        trailCommissionRate: partner.trailCommissionRate?.toString() ?? null,
                      }}
                    />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "activity",
            label: "Activity",
            content: (
              <div id="activity" className="space-y-2">
                <Card>
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    <p className="text-sm">Activity log coming soon</p>
                    <p className="text-xs mt-1">This will show timeline of changes to partner record</p>
                  </CardContent>
                </Card>
              </div>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4">
            {/* Key Dates */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{partner.createdAt ? new Date(partner.createdAt).toLocaleDateString("en-AU") : "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>{partner.updatedAt ? new Date(partner.updatedAt).toLocaleDateString("en-AU") : "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contacts Referred</span>
                  <span>{referredContacts.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Accounts Referred</span>
                  <span>{referredAccounts.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deals Referred</span>
                  <span>{referredDeals.length}</span>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-destructive mb-1.5">DANGER ZONE</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Permanently delete this partner and all associated data.
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Partner
              </Button>
            </div>
          </div>
        }
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the partner "{partner.partnerName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
